// «Viendo ahora» on the page (shared/API.md §39): the in-memory viewer table, and this tab's own
// heartbeats. Never persisted; cleared on a socket reconnect and re-read with GET.
//
// A scope (channel timeline, or one thread) is a TARGET while its pane is mounted — the
// conversation and the thread pane each register their own, so a channel with its thread open sends
// both. A target is VISIBLE while the tab is in front (`visibilityState === 'visible'`) AND has focus;
// visible targets POST at once and every 20 s; a target leaving (unmount) or the tab going hidden /
// losing focus sends DELETE at once; `pagehide` sends it with `fetch(keepalive)` (the worker is
// already gone). The server forgets an entry 50 s after its last heartbeat anyway.
import { useEffect, useSyncExternalStore } from 'react';
import { data } from '../../../bridge/client';
import { VIEWING_API, VIEWING_HEARTBEAT_MS, ViewingPresence, type Viewer, type ViewingChange } from '../../../core/viewingPresence';
import type { ViewingAnswer } from '../../../data/features/viewing';
import { keepaliveRequest } from '../../keepalive';

interface Target {
  channelId: string;
  rootId: string;
  refs: number;
  /** A POST went out and no DELETE since: the server counts us as looking. */
  beating: boolean;
  /** 403 (not a member) / 404 (no plugin): stop asking for this mount. */
  blocked: boolean;
}

const EMPTY: Viewer[] = [];
let table: ReadonlyMap<string, Viewer[]> = new Map();
const listeners = new Set<() => void>();
const targets = new Map<string, Target>();
let timer: ReturnType<typeof setInterval> | null = null;
let wired = false;
let lastVisible = false;
let wasConnected = false;

function publish(next: ReadonlyMap<string, Viewer[]>): void {
  if (next === table) return;
  table = next;
  for (const l of listeners) l();
}

function apply(change: ViewingChange): void {
  // Only scopes on screen are kept: the event goes to every member of the channel.
  if (!targets.has(ViewingPresence.key(change.channelId, change.rootId))) return;
  publish(ViewingPresence.reduce(table, change));
}

function answered(target: Target, answer: ViewingAnswer): void {
  if ('viewers' in answer) {
    apply({ channelId: target.channelId, rootId: target.rootId, viewers: answer.viewers });
    return;
  }
  if (answer.error === 'forbidden' || answer.error === 'absent') {
    target.blocked = true;
    target.beating = false;
  }
}

/** The tab is in front and focused (desktop "window key"). */
function pageVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus();
}

function beat(target: Target): void {
  if (target.blocked) return;
  target.beating = true;
  void data.call('viewingBeat', target.channelId, target.rootId).then(
    (answer) => answered(target, answer),
    () => undefined,
  );
}

function stop(target: Target): void {
  if (!target.beating) return;
  target.beating = false;
  void data.call('viewingStop', target.channelId, target.rootId).catch(() => undefined);
}

function read(target: Target): void {
  if (target.blocked) return;
  void data.call('viewingRead', target.channelId, target.rootId).then(
    (answer) => answered(target, answer),
    () => undefined,
  );
}

function tick(): void {
  if (!pageVisible()) return;
  for (const target of targets.values()) beat(target);
}

function onVisibility(): void {
  const visible = pageVisible();
  if (visible === lastVisible) return;
  lastVisible = visible;
  for (const target of targets.values()) (visible ? beat : stop)(target);
}

function wire(): void {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  lastVisible = pageVisible();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onVisibility);
  window.addEventListener('blur', onVisibility);
  window.addEventListener('pagehide', () => {
    for (const target of targets.values()) {
      if (!target.beating) continue;
      target.beating = false;
      keepaliveRequest('DELETE', VIEWING_API, { channel_id: target.channelId, root_id: target.rootId });
    }
  });
  data.onSignal((s) => {
    if (s.kind === 'viewing') apply(s.change);
    else if (s.kind === 'connection') {
      const connected = s.state.kind === 'connected';
      // Reconnected: events were missed — forget everything, re-read what is on screen, and beat
      // again (the server may have expired us while the socket was down).
      if (connected && wasConnected === false && targets.size) {
        publish(new Map());
        for (const target of targets.values()) {
          target.blocked = false;
          read(target);
          if (pageVisible()) beat(target);
        }
      }
      wasConnected = connected;
    }
  });
}

function ensureTimer(): void {
  if (targets.size && !timer) timer = setInterval(tick, VIEWING_HEARTBEAT_MS);
  if (!targets.size && timer) {
    clearInterval(timer);
    timer = null;
  }
}

/** Registers a scope as on screen while the calling pane is mounted. `channelId` '' = nothing. */
export function useViewingHeartbeat(channelId: string, rootId: string): void {
  useEffect(() => {
    if (!channelId) return;
    wire();
    const key = ViewingPresence.key(channelId, rootId);
    let target = targets.get(key);
    if (target) target.refs++;
    else {
      target = { channelId, rootId, refs: 1, beating: false, blocked: false };
      targets.set(key, target);
      read(target);
      if (pageVisible()) beat(target);
    }
    ensureTimer();
    const mine = target;
    return () => {
      mine.refs--;
      if (mine.refs > 0) return;
      targets.delete(key);
      stop(mine);
      ensureTimer();
      if (table.has(key)) {
        const next = new Map(table);
        next.delete(key);
        publish(next);
      }
    };
  }, [channelId, rootId]);
}

/** The scope's current set (me included — the views filter), [] when nobody. */
export function useViewers(channelId: string, rootId: string): Viewer[] {
  const key = ViewingPresence.key(channelId, rootId);
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => table.get(key) ?? EMPTY,
  );
}

/** Verification hook (capture scripts): the table as plain data. */
export function viewingDebugTable(): Record<string, Viewer[]> {
  return Object.fromEntries(table);
}
